import { useState, type ReactNode } from "react";
import { useDispatch } from "react-redux";
import OnboardingOverlay from "./OnboardingOverlay";
import { fetchOnboardingInitialData, type OnboardingFormData } from "../../api/getOnboardingInitialData";
import { openAuthOverlay } from "../../components/redux/AuthActions";
import { OnboardingOverlayContext, type LoginAfterRegistration } from "../../hooks/OnboardingOverlayContext";

interface Props {
  children: ReactNode;
  loginAfterRegistration: LoginAfterRegistration;
  loginErrorComponent?: ReactNode;
}

export const OnboardingOverlayProvider = ({ children, loginAfterRegistration, loginErrorComponent }: Props) => {
  const [openOnboarding, setOpenOnboarding] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const loadOnboardingData = async (token: string) => {
    setLoading(true);

    try {
      const { data } = await fetchOnboardingInitialData(token);

      setOnboardingData(data);
      setOpenOnboarding(true);
    } finally {
      setLoading(false);
    }
  };

  const onSignUpClick = () => {
    setOpenOnboarding(false);
    dispatch(openAuthOverlay("register"));
  };

  return (
    <OnboardingOverlayContext.Provider
      value={{
        openOnboarding: async (token: string) => await loadOnboardingData(token),
        closeOnboarding: () => setOpenOnboarding(false),
      }}
    >
      {children}

      {openOnboarding && !loading && onboardingData != null && (
        <OnboardingOverlay
          data={onboardingData}
          loginAfterRegistration={loginAfterRegistration}
          loginErrorComponent={loginErrorComponent}
          onSignUpClick={onSignUpClick}
        />
      )}
    </OnboardingOverlayContext.Provider>
  );
};
